import { useState, useEffect, useRef } from 'react';
import { Loader2, Save, User, AlertCircle } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { PhoneInput, parsePhoneString, formatPhoneString, type PhoneValue } from '@/components/ui/PhoneInput';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { DateInput } from '@/components/ui/DateInput';
import { getCountryNames, getStateNames, getCityNames, countryIdByName, stateIdByName, cityIdByName } from '@/lib/geoApi';
import { SelectField } from '@/components/ui/SelectField';

const LANGUAGE_OPTIONS = ['English', 'Hindi', 'Marathi', 'Tamil', 'Telugu', 'Gujarati', 'Bengali', 'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Urdu', 'Arabic', 'French', 'German', 'Spanish', 'Other'];

// Country / state / city all come from our own DB master via lib/geoApi.
const fetchStates = getStateNames;
const fetchCities = getCityNames;

// ── Component ────────────────────────────────────────────────────────────────

const LABEL = 'text-xs font-semibold text-slate-600 uppercase tracking-wide';
const INPUT = 'mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';

export function ProfilePage() {
  const { user: me } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    firstName: '',
    lastName: '',
    gender: '',
    dob: '',
    language: '',
    address: '',
    country: '',
    state: '',
    city: '',
    pincode: '',
  });
  const [phone, setPhone] = useState<PhoneValue>({ countryCode: '+91', number: '' });

  // Geo state
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [geoError, setGeoError] = useState('');
  const statesAbort = useRef<AbortController | null>(null);
  const citiesAbort = useRef<AbortController | null>(null);

  // Load countries from the DB geo master on mount.
  useEffect(() => {
    setLoadingCountries(true);
    getCountryNames().then(setCountries).catch(() => setGeoError('Failed to load countries')).finally(() => setLoadingCountries(false));
  }, []);

  // Load user data
  useEffect(() => {
    if (!me?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/users/${me.id}`);
        setForm({
          name: data.name ?? '',
          firstName: data.firstName ?? '',
          lastName: data.lastName ?? '',
          gender: data.gender ?? '',
          dob: data.dob ? data.dob.slice(0, 10) : '',
          language: data.language ?? '',
          address: data.address ?? '',
          country: data.country ?? '',
          state: data.state ?? '',
          city: data.city ?? '',
          pincode: data.pincode ?? '',
        });
        setPhone(parsePhoneString(data.phone ?? ''));
      } catch {
        toast('error', 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [me?.id]);

  // Load states when country changes
  useEffect(() => {
    if (!form.country) { setStates([]); setCities([]); return; }
    statesAbort.current?.abort();
    setLoadingStates(true);
    setStates([]);
    setCities([]);
    setGeoError('');
    fetchStates(form.country)
      .then(setStates)
      .catch(err => { if (err.name !== 'AbortError') setGeoError(`Could not load states for "${form.country}".`); })
      .finally(() => setLoadingStates(false));
  }, [form.country]);

  // Load cities when state changes
  useEffect(() => {
    if (!form.country || !form.state) { setCities([]); return; }
    citiesAbort.current?.abort();
    setLoadingCities(true);
    setCities([]);
    setGeoError('');
    fetchCities(form.country, form.state)
      .then(setCities)
      .catch(err => { if (err.name !== 'AbortError') setGeoError(`Could not load cities for "${form.state}".`); })
      .finally(() => setLoadingCities(false));
  }, [form.country, form.state]);

  const handleCountryChange = (val: string) => setForm(f => ({ ...f, country: val, state: '', city: '' }));
  const handleStateChange = (val: string) => setForm(f => ({ ...f, state: val, city: '' }));
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        phone: formatPhoneString(phone),
        countryId: countryIdByName(form.country) ?? undefined,
        stateId: stateIdByName(form.country, form.state) ?? undefined,
        cityId: cityIdByName(form.country, form.state, form.city) ?? undefined,
      };
      await api.patch(`/users/${me!.id}`, payload);
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.name = form.name;
        localStorage.setItem('user', JSON.stringify(parsed));
      }
      toast('success', 'Profile updated');
    } catch {
      toast('error', 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
          <p className="text-sm text-slate-500 mt-1">Update your personal information</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          {/* User info header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">{me?.name}</p>
              <p className="text-sm text-slate-500">{me?.email} <span className="text-xs text-slate-400 ml-1">(cannot be changed)</span></p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Name fields */}
            <Field label="Full Name" value={form.name} onChange={v => set('name', v)} required />
            <Field label="First Name" value={form.firstName} onChange={v => set('firstName', v)} />
            <Field label="Last Name" value={form.lastName} onChange={v => set('lastName', v)} />

            {/* Phone with country code */}
            <div>
              <label className={LABEL}>Phone</label>
              <div className="mt-1.5">
                <PhoneInput value={phone} onChange={setPhone} />
              </div>
            </div>

            {/* Gender */}
            <div>
              <label className={LABEL}>Gender</label>
              <SelectField value={form.gender} onChange={e => set('gender', e.target.value)} className={INPUT}>
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </SelectField>
            </div>

            <Field label="Date of Birth" value={form.dob} onChange={v => set('dob', v)} type="date" />

            {/* Language multi-select */}
            <div>
              <label className={LABEL}>Language</label>
              <div className="mt-1.5 flex flex-wrap gap-1.5 border border-slate-200 rounded-lg px-2.5 py-2 min-h-[42px] cursor-pointer"
                onClick={e => { const t = e.currentTarget.querySelector('details'); if (t) t.open = !t.open; }}>
                {(form.language ? form.language.split(',').filter(Boolean) : []).map(lang => (
                  <span key={lang} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs font-medium px-2 py-0.5 rounded-md">
                    {lang}
                    <button type="button" onClick={e => { e.stopPropagation(); set('language', form.language.split(',').filter(l => l !== lang).join(',')); }}
                      className="hover:text-red-500">&times;</button>
                  </span>
                ))}
                {!form.language && <span className="text-sm text-slate-400">Select languages</span>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {LANGUAGE_OPTIONS.map(lang => {
                  const selected = form.language.split(',').includes(lang);
                  return (
                    <button key={lang} type="button"
                      onClick={() => {
                        const langs = form.language ? form.language.split(',').filter(Boolean) : [];
                        if (selected) set('language', langs.filter(l => l !== lang).join(','));
                        else set('language', [...langs, lang].join(','));
                      }}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        selected ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      {lang}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="col-span-2">
              <Field label="Address" value={form.address} onChange={v => set('address', v)} />
            </div>

            {/* Geo error */}
            {geoError && (
              <div className="col-span-2 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                {geoError}
              </div>
            )}

            {/* Country */}
            <div>
              <label className={LABEL}>Country</label>
              <div className="mt-1.5">
                <SearchableSelect
                  options={countries}
                  value={form.country}
                  onChange={handleCountryChange}
                  placeholder={loadingCountries ? 'Loading...' : 'Select Country'}
                  loading={loadingCountries}
                  disabled={loadingCountries}
                />
              </div>
            </div>

            {/* State */}
            <div>
              <label className={LABEL}>
                State
                {loadingStates && <Loader2 className="inline ml-1 h-3 w-3 animate-spin text-indigo-500" />}
              </label>
              <div className="mt-1.5">
                <SearchableSelect
                  options={states}
                  value={form.state}
                  onChange={handleStateChange}
                  placeholder={!form.country ? 'Select country first' : loadingStates ? 'Loading...' : states.length === 0 ? 'N/A' : 'Select State'}
                  loading={loadingStates}
                  disabled={!form.country || loadingStates}
                />
              </div>
            </div>

            {/* City */}
            <div>
              <label className={LABEL}>
                City
                {loadingCities && <Loader2 className="inline ml-1 h-3 w-3 animate-spin text-indigo-500" />}
              </label>
              <div className="mt-1.5">
                {cities.length > 0 ? (
                  <SearchableSelect
                    options={cities}
                    value={form.city}
                    onChange={val => set('city', val)}
                    placeholder={!form.state ? 'Select state first' : loadingCities ? 'Loading...' : 'Select City'}
                    loading={loadingCities}
                    disabled={!form.state || loadingCities}
                  />
                ) : (
                  <input
                    className={INPUT}
                    placeholder={!form.state ? 'Select state first' : 'Type city'}
                    value={form.city}
                    onChange={e => set('city', e.target.value)}
                    disabled={!form.state && !loadingCities}
                  />
                )}
              </div>
            </div>

            <Field label="Zipcode" value={form.pincode} onChange={v => set('pincode', v)} />
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, value, onChange, type = 'text', required }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean;
}) {
  return (
    <div>
      <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">{label}</label>
      {type === 'date' ? (
        <DateInput className="mt-1.5 w-full" value={value} onChange={onChange} />
      ) : (
        <input
          type={type} value={value} onChange={e => onChange(e.target.value)} required={required}
          className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      )}
    </div>
  );
}
